import { Select, Text, TextInput, Stack } from '@mantine/core';
import { useController, type Control, type FieldErrors } from 'react-hook-form';
import { APP_CONSTRAINTS } from '@/lib/constants/app';
import { ROKU_AGE_RATINGS } from '@/lib/constants/rokuStore';
import type { AppListingFormSchemaData } from '../schemas/AppListingForm.schema';

interface AppListingFieldsProps {
  control: Control<AppListingFormSchemaData>;
  errors: FieldErrors<AppListingFormSchemaData>;
  disabled?: boolean;
}

/**
 * The shared OTT store-listing field set (D-2.4) — rendered identically by the
 * Store Listing sub-tab and the Roku setup wizard.
 */
export function AppListingFields({ control, errors, disabled }: AppListingFieldsProps) {
  const { field: privacyField } = useController({ control, name: 'privacyPolicyUrl' });
  const { field: termsField } = useController({ control, name: 'termsUrl' });
  const { field: ageRatingField } = useController({ control, name: 'ageRating' });
  const { field: supportEmailField } = useController({ control, name: 'supportEmail' });
  const { field: supportPhoneField } = useController({ control, name: 'supportPhone' });
  const { field: supportUrlField } = useController({ control, name: 'supportUrl' });
  const { field: appAdsTxtField } = useController({ control, name: 'appAdsTxtUrl' });

  return (
    <Stack gap="lg">
      <TextInput
        label="Privacy Policy URL"
        placeholder="https://..."
        value={privacyField.value}
        onChange={privacyField.onChange}
        onBlur={privacyField.onBlur}
        maxLength={APP_CONSTRAINTS.LISTING_URL_MAX}
        error={errors.privacyPolicyUrl?.message}
        disabled={disabled}
      />

      <TextInput
        label="Terms of Service URL"
        placeholder="https://..."
        value={termsField.value}
        onChange={termsField.onChange}
        onBlur={termsField.onBlur}
        maxLength={APP_CONSTRAINTS.LISTING_URL_MAX}
        error={errors.termsUrl?.message}
        disabled={disabled}
      />

      {/* Curated portal picklist (rokuStore.ts): storage stays TEXT, so a
          legacy/drifted stored value still renders — merged into the data. */}
      <Select
        label="Age Rating"
        placeholder="Select a rating"
        description="Pick 'Content Not Rated' if any of your content has no rating"
        data={
          ageRatingField.value && !ROKU_AGE_RATINGS.some((r) => r.value === ageRatingField.value)
            ? [...ROKU_AGE_RATINGS, { value: ageRatingField.value, label: ageRatingField.value }]
            : [...ROKU_AGE_RATINGS]
        }
        value={ageRatingField.value || null}
        onChange={(v) => ageRatingField.onChange(v ?? '')}
        onBlur={ageRatingField.onBlur}
        clearable
        error={errors.ageRating?.message}
        disabled={disabled}
      />

      <Text fw={600} size="md">
        Support Contact
      </Text>

      <TextInput
        label="Support Email"
        placeholder="support@example.com"
        value={supportEmailField.value}
        onChange={supportEmailField.onChange}
        onBlur={supportEmailField.onBlur}
        maxLength={APP_CONSTRAINTS.LISTING_EMAIL_MAX}
        error={errors.supportEmail?.message}
        disabled={disabled}
      />

      <TextInput
        label="Support Phone"
        placeholder="+1 555 555 5555"
        value={supportPhoneField.value}
        onChange={supportPhoneField.onChange}
        onBlur={supportPhoneField.onBlur}
        maxLength={APP_CONSTRAINTS.LISTING_PHONE_MAX}
        error={errors.supportPhone?.message}
        disabled={disabled}
      />

      <TextInput
        label="Support URL"
        placeholder="https://..."
        value={supportUrlField.value}
        onChange={supportUrlField.onChange}
        onBlur={supportUrlField.onBlur}
        maxLength={APP_CONSTRAINTS.LISTING_URL_MAX}
        error={errors.supportUrl?.message}
        disabled={disabled}
      />

      <Text fw={600} size="md">
        Advertising
      </Text>

      <TextInput
        label="app-ads.txt URL"
        placeholder="https://example.com/app-ads.txt"
        description="Required by ad partners for ad-enabled apps"
        value={appAdsTxtField.value}
        onChange={appAdsTxtField.onChange}
        onBlur={appAdsTxtField.onBlur}
        maxLength={APP_CONSTRAINTS.LISTING_URL_MAX}
        error={errors.appAdsTxtUrl?.message}
        disabled={disabled}
      />
    </Stack>
  );
}
